import { useMutation } from "@apollo/client";
import { StackScreenProps } from "@react-navigation/stack";
import {
  ModalCloseButton,
  Page,
  Stack,
  TextField,
} from "@towersystems/roam-common-native";
import { TerminalClosure } from "@towersystems/roam-common/lib/generated-types";
import { useEffect } from "react";
import { ShiftRootParamList } from "../../../../types";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import { valueAsMoney } from "../../../../utilities/money";
import { MUTATION_OPEN_TERMINAL_SHIFT } from "../../../../utilities/terminal-shift/constants";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { useEndOfShiftContextProvider } from "../../utils/shift/hooks";
import { useCollectCashForm } from "../../utils/use-collect-cash-form";
import { useTranslation } from "../../../../utilities/i18n";

export interface StartNewShiftScreenProps
  extends StackScreenProps<ShiftRootParamList, "StartNewShiftScreen"> {}

export const StartNewShiftScreen = ({
  navigation,
}: StartNewShiftScreenProps) => {
  const { t } = useTranslation("end_of_shift");
  const { shiftTerminal } = useEndOfShiftContextProvider();
  const globalSettings = useGlobalSettingsProvider();

  const [f] = useMutation<{ openTerminal: TerminalClosure }>(
    MUTATION_OPEN_TERMINAL_SHIFT
  );

  const {
    setAmount,
    amount,
    isValid,
    note,
    setNote,
    submitting,
    handleOnSubmit,
  } = useCollectCashForm({
    onSubmit: handleOnConfirm,
    defaultValues: {
      amount: valueAsMoney(
        globalSettings.defaultTerminalClosureFloat.toString()
      ),
    },
  });

  const shouldFocus = useHasTransitionEnded(navigation);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => {
        return (
          <ModalCloseButton
            type="Continue"
            onPress={handleOnSubmit}
            disabled={!isValid || submitting}
          />
        );
      },
    });
  }, [handleOnSubmit, submitting, isValid]);

  async function handleOnConfirm() {
    return f({
      variables: {
        input: {
          terminalId: shiftTerminal.id,
          openFloatAmount: parseFloat(amount),
          //note: note,
        },
      },
    })
      .then(async (response) => {
        if (!response.data?.openTerminal) {
          throw new Error(t("error.no_terminal_closure.message"));
        }
        const { terminalOpenDate, terminalCloseDate, id } =
          response.data.openTerminal;
        return shiftTerminal
          .updateClosureDetails({
            openTerminalClosureId: id,
            terminalCloseTime: terminalCloseDate,
            terminalOpenTime: terminalOpenDate,
          })
          .then(() => {
            return navigation.reset({
              index: 0,
              routes: [
                { name: "ShiftLandingScreen" },
                { name: "ShiftHistoryScreen" },
                {
                  name: "OpenShiftScreen",
                },
              ],
            });
          });
      })
      .catch((err) => console.log(err));
  }

  return (
    <Page>
      <Stack vertical>
        <TextField
          label={t("form.starting_amount.label")}
          placeholder={t("form.starting_amount.placeholder")}
          value={amount}
          onChangeText={setAmount}
          autoFocus={shouldFocus}
          formatterType={isValid ? "money" : undefined}
          keyboardType="numeric"
          validationMessage={isValid ? undefined : "Invalid Amount"}
        />
        <TextField
          label={t("form.note.label")}
          placeholder={t("form.note.label")}
          value={note}
          onChangeText={setNote}
        />
      </Stack>
    </Page>
  );
};
